import { createSlice } from '@reduxjs/toolkit';
import {
    getAllClients,
    getClientSummary,
    updateTrialInfo,
    deleteDeployment,
    extendTrialPeriod,
    processUpdateClient,
    addContactInfo,
    addPaymentInfo,
    editCurrentPlan,
    addSubscriptionAddOn,
    deleteContactInfo,
    updateContactInfo,
    trialToFullDeployment
} from 'api/clients';
import { dispatch } from '../index';
import { store } from 'store';
import { accessAsAdmin } from 'api/users';

const initialState = {
    list: [],
    total: 0,
    currentPage: 1,
    rowsPerPage: 10,
    isLoading: false,
    searchQuery: '',
    showDisabled: false,
    tags: [],
    sorting: {
        field: 'currentEndDate',
        value: -1 // 1 = asc; -1 = desc
    },
    renewalDateFilter: {
        startDate: null,
        endDate: null
    },
    summary: {
        active: 0,
        inactive: 0,
        new: 0,
        isLoading: false
    },
    selectedClient: {
        data: null,
        isLoading: false
    },
    logoUploading: false,
    users: [],
    showTrial: false,
    showOnlyInactive: false,
    showOnlyDeidentified: false
};

const slice = createSlice({
    name: 'clients',
    initialState,
    reducers: {
        setClientsList(state, action) {
            state.list = action.payload.data;
            state.total = action.payload.total;
            state.isLoading = false;
        },
        setClientCurrentPage(state, action) {
            state.currentPage = action.payload;
        },
        setClientRowsPerPage(state, action) {
            state.rowsPerPage = action.payload;
        },
        setClientLoading(state, action) {
            state.isLoading = action.payload;
        },
        setClientSummaryLoading(state, action) {
            state.summary.isLoading = action.payload;
        },
        setClientSummary(state, action) {
            state.summary = action.payload.data;
            state.summary.isLoading = false;
        },
        resetClientState(state) {
            return { ...initialState, showTrial: state.showTrial }; // prevent from resetting showTrial flag
        },
        setTrialDisplay(state, action) {
            state.showTrial = action.payload;
        },
        setSorting(state, action) {
            state.currentPage = 1; // reset to page 1
            state.sorting = action.payload;
        },
        setSearchQuery(state, action) {
            state.currentPage = 1; // reset to page 1
            state.searchQuery = action.payload;
        },
        setShowDisabled(state, action) {
            state.currentPage = 1; // reset to page 1
            state.showDisabled = action.payload;
        }
    }
});

export default slice.reducer;

// clients table pagination
export function clientCurrentPage(val) {
    return async () => {
        await dispatch(slice.actions.setClientCurrentPage(val));
        await getListClients();
    };
}
export function setClientSorting(val) {
    return async () => {
        dispatch(slice.actions.setSorting(val));
        await getListClients();
    };
}

export function setTrialList(val) {
    return async () => {
        dispatch(slice.actions.setTrialDisplay(val));
    };
}

export function clientRowsPerPage(val) {
    return async () => {
        dispatch(slice.actions.setClientRowsPerPage(val));
    };
}

// clients summary
export function fetchClientsSummary() {
    return async () => {
        await getListClients();
        try {
            dispatch(slice.actions.setClientSummaryLoading(true));
            const storeData = store.getState();
            const { showTrial } = storeData.clients;

            const summary = await getClientSummary(showTrial);
            dispatch(slice.actions.setClientSummary(summary));
        } catch (err) {
            console.log(err);
            dispatch(slice.actions.setClientLoading(false));
            dispatch(slice.actions.setClientSummaryLoading(false));
            throw new Error(err.message);
        }
    };
}

// initial load clients list data, reset all the filters
export function initialLoadClientData() {
    return async () => {
        await dispatch(slice.actions.resetClientState());
        await getListClients();
    };
}

// clients list search query
export function searchClientsList(query) {
    return async () => {
        dispatch(slice.actions.setSearchQuery(query));
        await getListClients();
    };
}

// clients list toggle for disabled clients
export function showDisabledClients(val) {
    return async () => {
        dispatch(slice.actions.setShowDisabled(val));
        await getListClients();
    };
}

export function updateTrial(trialId, trialData) {
    return async () => {
        try {
            const response = await updateTrialInfo(trialId, trialData);
            await getListClients();
            return response;
        } catch (err) {
            dispatch(slice.actions.setClientLoading(false));
            throw err;
        }
    };
}

// soft delete deployment
export function softDeleteDeployment(deployment) {
    return async () => {
        try {
            await deleteDeployment(deployment);
            await getListClients();
        } catch (err) {
            throw new Error(err.message);
        }
    };
}

export function extendTrial(params) {
    return async () => {
        try {
            const response = await extendTrialPeriod(params);
            await getListClients();
            return response;
        } catch (err) {
            dispatch(slice.actions.setClientLoading(false));
            throw err;
        }
    };
}

export function updateClient(params) {
    return async () => {
        try {
            const response = await processUpdateClient(params);
            await getListClients();
            return response;
        } catch (err) {
            dispatch(slice.actions.setClientLoading(false));
            throw err;
        }
    };
}

export function addClientPaymentInfo(deployment, params) {
    return async () => {
        try {
            const response = await addPaymentInfo(deployment, params);
            await getListClients();
            return response;
        } catch (err) {
            dispatch(slice.actions.setClientLoading(false));
            throw err;
        }
    };
}

export function editClientCurrentPlan(deployment, params) {
    return async () => {
        try {
            const response = await editCurrentPlan(deployment, params);
            await getListClients();
            return response;
        } catch (err) {
            dispatch(slice.actions.setClientLoading(false));
            throw err;
        }
    };
}

export function addClientSubscriptionAddOn(deployment, params) {
    return async () => {
        try {
            const response = await addSubscriptionAddOn(deployment, params);
            await getListClients();
            return response;
        } catch (err) {
            dispatch(slice.actions.setClientLoading(false));
            throw err;
        }
    };
}

export function addClientContact(deployment, params) {
    return async () => {
        try {
            const response = await addContactInfo(deployment, params);
            await getListClients();
            return response;
        } catch (err) {
            dispatch(slice.actions.setClientLoading(false));
            throw err;
        }
    };
}

export function updateClientContact(deployment, params) {
    return async () => {
        try {
            const response = await updateContactInfo(deployment, params);
            await getListClients();
            return response;
        } catch (err) {
            dispatch(slice.actions.setClientLoading(false));
            throw err;
        }
    };
}

export function deleteClientContact(deployment, contactId) {
    return async () => {
        try {
            const response = await deleteContactInfo(deployment, contactId);
            await getListClients();
            return response;
        } catch (err) {
            dispatch(slice.actions.setClientLoading(false));
            throw err;
        }
    };
}

export function upgradeTrialToFullDeployment(params) {
    return async () => {
        try {
            const response = await trialToFullDeployment(params);
            await getListClients();
            return response;
        } catch (err) {
            dispatch(slice.actions.setClientLoading(false));
            throw err;
        }
    };
}

// fetch all clients list function
async function getListClients() {
    try {
        dispatch(slice.actions.setClientLoading(true));
        const storeData = store.getState();
        const {
            currentPage,
            sorting,
            rowsPerPage,
            searchQuery,
            showDisabled,
            tags,
            renewalDateFilter,
            showTrial,
            showOnlyInactive,
            showOnlyDeidentified
        } = storeData.clients;

        const clients = await getAllClients({
            perPage: rowsPerPage,
            pageNo: currentPage,
            query: searchQuery,
            showDisabled,
            tags,
            startDate: renewalDateFilter?.startDate || '',
            endDate: renewalDateFilter?.endDate || '',
            isTrial: showTrial,
            sortField: sorting?.field,
            sorting: sorting.value,
            showOnlyInactive,
            showOnlyDeidentified
        });
        dispatch(slice.actions.setClientsList(clients?.data));
    } catch (err) {
        console.log(err);
        dispatch(slice.actions.setClientLoading(false));
        throw new Error(err.message);
    }
}

// access deployment as admin
export function accessDeploymentAsAdmin(data) {
    return async () => {
        try {
            return await accessAsAdmin(data);
        } catch (err) {
            throw new Error(err.message);
        }
    };
}
